import { Box, Typography, styled } from "@mui/material"
import type React from "react"
import { createPortal } from "react-dom"
import { formatDateOnly } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import { NwssTooltipInfoRow } from "./NwssTooltipInfoRows"
import type { NwssSignalSample } from "./nwssSampleTypes"
import type { NwssDrawerSampleScope } from "./nwssDisplayFormat"
import type {
  NwssRollingDetectionRateData,
  NwssRollingRatePoint,
} from "./nwssWeeklyCharts"

const TooltipBody = styled(Box)(() => ({
  padding: 12,
  maxWidth: 280,
  backgroundColor: extraColors.dark,
  border: `1px solid ${extraColors.translucent8}`,
  borderRadius: 4,
  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
}))

const TooltipHeader = styled(Typography)(() => ({
  fontWeight: 800,
  color: extraColors.translucent50,
  marginBottom: 8,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontSize: "0.65rem",
}))

const TooltipSubheader = styled(Typography)(() => ({
  fontWeight: 700,
  color: extraColors.white,
  marginBottom: 8,
  display: "block",
  fontSize: "0.75rem",
}))

export interface NwssRollingRatePointDetailProps {
  point: NwssRollingRatePoint
  panel: NwssRollingDetectionRateData
  samples: NwssSignalSample[]
  reportingCounties?: string[] | null
  lookbackMonths?: number
  sampleScope?: NwssDrawerSampleScope
}

export const NwssRollingRatePointDetail: React.FC<
  NwssRollingRatePointDetailProps
> = ({ point, panel, sampleScope = "historic" }) => {
  const useDetectionCount = sampleScope === "event"

  return (
    <TooltipBody>
      <TooltipHeader>Collection date</TooltipHeader>
      <TooltipSubheader>{formatDateOnly(point.date)}</TooltipSubheader>
      {panel.reportingCounty && (
        <Typography
          variant="caption"
          sx={{ color: extraColors.translucent50, display: "block", mb: 1 }}
        >
          {panel.reportingCounty}
        </Typography>
      )}
      {useDetectionCount ? (
        <>
          <NwssTooltipInfoRow
            label="14-day rolling detection count"
            value={String(point.windowDetectionCount)}
            valueSx={{ fontWeight: 700 }}
          />
          <NwssTooltipInfoRow
            label="Trailing 14 days"
            value={`${point.windowSampleCount} event detection sample${
              point.windowSampleCount === 1 ? "" : "s"
            }`}
          />
        </>
      ) : (
        <>
          <NwssTooltipInfoRow
            label="14-day rolling detection rate"
            value={`${Math.round(point.ratePct)}%`}
            valueSx={{ fontWeight: 700 }}
          />
          <NwssTooltipInfoRow
            label="Trailing 14 days"
            value={`${point.windowDetectionCount} detection${
              point.windowDetectionCount === 1 ? "" : "s"
            } of ${point.windowSampleCount} sample${
              point.windowSampleCount === 1 ? "" : "s"
            }`}
          />
        </>
      )}
      <Typography
        variant="caption"
        sx={{
          color:
            point.sameDayDetections > 0
              ? extraColors.status.redLight
              : extraColors.translucent50,
          display: "block",
          mt: 1,
          fontWeight: point.sameDayDetections > 0 ? 700 : 400,
        }}
      >
        {point.sameDayDetections === 0
          ? "No detections on this collection date"
          : `${point.sameDayDetections} detection${
              point.sameDayDetections === 1 ? "" : "s"
            } on this collection date`}
      </Typography>
    </TooltipBody>
  )
}

export interface NwssRollingRateHoverPopperProps extends NwssRollingRatePointDetailProps {
  open: boolean
  anchorPosition: { top: number; left: number } | null
}

const CURSOR_TOOLTIP_OFFSET_PX = 12

/** Fixed at the pointer so it is not clipped or mis-positioned by the drawer. */
export const NwssRollingRateHoverPopper: React.FC<
  NwssRollingRateHoverPopperProps
> = ({ open, anchorPosition, ...detailProps }) => {
  if (!open || anchorPosition === null) return null

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        top: anchorPosition.top + CURSOR_TOOLTIP_OFFSET_PX,
        left: anchorPosition.left + CURSOR_TOOLTIP_OFFSET_PX,
        zIndex: theme => theme.zIndex.modal,
        pointerEvents: "none",
      }}
    >
      <NwssRollingRatePointDetail {...detailProps} />
    </Box>,
    document.body
  )
}
